import type {TuiCountryIsoCode, TuiLanguage} from '@taiga-ui/i18n/types';

// addon-commerce.ts
import type {TuiLanguageCommerce} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_ADDON_COMMERCE: TuiLanguageCommerce = {
    cardNumber: ['Nummer', 'Kortnummer'],
    cardExpiry: ['Udløb', 'Udløbsdato'],
};

// layout.ts
import type {TuiLanguageLayout} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_LAYOUT: TuiLanguageLayout = {
    inputSearch: {
        popular: 'Populært',
        history: 'Seneste',
        placeholder: 'Søg',
        hotkey: 'for at søge',
        all: 'Alle',
        empty: 'Ingen resultater',
    },
};

// kit.ts
import type {TuiLanguageKit} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_KIT: TuiLanguageKit = {
    cancel: 'Annuller',
    done: 'Færdig',
    more: 'Mere',
    otherDate: 'Anden dato...',
    showAll: 'Vis alle',
    hide: 'Skjul',
    mobileCalendarTexts: [
        'Vælg dag',
        'Vælg periode',
        'Vælg dage',
    ],
    range: ['Fra', 'Til'],
    countTexts: ['Mere', 'Mindre'],
    time: {
        'HH:MM': 'HH:MM', 
        'HH:MM AA': 'HH:MM AA', 
        'HH:MM:SS': 'HH:MM:SS',
        'HH:MM:SS AA': 'HH:MM:SS AA',
        'HH:MM:SS.MSS': 'HH:MM:SS.MSS',
        'HH:MM:SS.MSS AA': 'HH:MM:SS.MSS AA',
    },
    dateTexts: {
        DMY: 'dag.måned.år', 
        MDY: 'måned.dag.år', 
        YMD: 'år.måned.dag', 
    },
    digitalInformationUnits: ['Byte', 'Kilobyte', 'Megabyte'],
    passwordTexts: ['Vis adgangskode', 'Skjul adgangskode'],
    copyTexts: ['Kopier', 'Kopieret'],
    shortCalendarMonths: [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'Maj',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Okt',
        'Nov',
        'Dec',
    ],
    pagination: ['Forrige side', 'Næste side'],
    fileTexts: {
        loadingError: 'Indlæsningsfejl',
        preview: 'Forhåndsvisning',
        remove: 'Fjern',
    },
    inputFileTexts: {
        defaultLabelSingle: 'eller slip den her',
        defaultLabelMultiple: 'eller slip dem her',
        defaultLinkSingle: 'Vælg fil',
        defaultLinkMultiple: 'Vælg filer',
        maxSizeRejectionReason: 'Filen er for stor',
        formatRejectionReason: 'Ugyldigt filformat',
        drop: 'Slip fil her',
        dropMultiple: 'Slip filer her',
    },
    multiSelectTexts: {
        all: 'Vælg alle',
        none: 'Vælg ingen',
    },
    confirm: {
        yes: 'Ja',
        no: 'Nej',
    },
    previewTexts: {
        rotate: 'Roter',
    },
    zoomTexts: {
        zoomOut: 'Zoom ud',
        zoomIn: 'Zoom ind',
        reset: 'Nulstil',
    },
    phoneSearch: 'Søg land eller kode',
};

// addon-editor.ts
import type {TuiLanguageEditor} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_ADDON_EDITOR: TuiLanguageEditor = {
    colorSelectorModeNames: ['Enkeltfarve', 'Farvegradient'],
    toolbarTools: {
        undo: 'Fortryd',
        redo: 'Gendan',
        font: 'Skrifttype',
        fontStyle: 'Skriftstil',
        fontSize: 'Skriftstørrelse',
        bold: 'Fed',
        italic: 'Kursiv',
        underline: 'Understreget',
        strikeThrough: 'Gennemstreget',
        justify: 'Justering',
        justifyLeft: 'Venstrejuster',
        justifyCenter: 'Centrer',
        justifyRight: 'Højrejuster',
        justifyFull: 'Lige marginer',
        list: 'Liste',
        indent: 'Forøg indrykning',
        outdent: 'Formindsk indrykning',
        unorderedList: 'Punktopstilling',
        orderedList: 'Nummereret liste',
        quote: 'Citat',
        foreColor: 'Tekstfarve',
        backColor: 'Baggrundsfarve',
        hiliteColor: 'Fremhævningsfarve',
        clear: 'Ryd',
        link: 'Link',
        attach: 'Vedhæft fil',
        tex: 'Indsæt TeX',
        code: 'Kode',
        image: 'Indsæt billede',
        insertHorizontalRule: 'Indsæt vandret linje',
        superscript: 'Hævet skrift',
        subscript: 'Sænket skrift',
        insertTable: 'Indsæt tabel',
        insertGroup: 'Indsæt gruppe',
        hiliteGroup: 'Fremhæv gruppe',
        removeGroup: 'Fjern gruppe',
        insertAnchor: 'Indsæt anker',
        mergeCells: 'Flet celler',
        splitCells: 'Opdel celler',
        rowsColumnsManaging: 'Håndter rækker og kolonner',
        cellColor: 'Cellefarve',
        setDetails: 'Angiv detaljer',
        removeDetails: "Fjern detaljer",
    },
    editorEditLink: {
        urlExample: 'eksempel.com',
        anchorExample: 'anker',
    },
    editorTableCommands: [
        ['Indsæt kolonne før', 'Indsæt kolonne efter'],
        ['Indsæt række før', 'Indsæt række efter'],
        ['Fjern kolonne', 'Fjern række'],
    ],
    editorCodeOptions: ['Kode i tekst', 'Kodeblok'],
    editorFontOptions: {
        small: 'Lille',
        large: 'Stor',
        normal: 'Normal',
        title: 'Titel',
        subtitle: 'Undertitel',
    },
};

// addon-table.ts
import type {TuiLanguageTable} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_ADDON_TABLE: TuiLanguageTable = {
    showHideText: 'Vis/Skjul',
    paginationTexts: {
        pages: 'Sider',
        linesPerPage: 'Rækker per side',
        of: 'af',
    },
};

// core.ts
import type {TuiLanguageCore} from '@taiga-ui/i18n/types';

export const TUI_DANISH_LANGUAGE_CORE: TuiLanguageCore = {
    months: [
        'Januar',
        'Februar',
        'Marts',
        'April',
        'Maj',
        'Juni',
        'Juli',
        'August',
        'September',
        'Oktober',
        'November',
        'December',
    ],
    close: 'Luk',
    clear: 'Ryd',
    nothingFoundMessage: 'Ingen resultater',
    defaultErrorMessage: 'Ugyldig værdi',
    spinTexts: ['Forrige', 'Næste'],
    shortWeekDays: ['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'],
    countries: {} as Record<TuiCountryIsoCode, string>,
};

export const TUI_DANISH_LANGUAGE: TuiLanguage = {
    name: 'danish',
    ...TUI_DANISH_LANGUAGE_CORE,
    ...TUI_DANISH_LANGUAGE_KIT,
    ...TUI_DANISH_LANGUAGE_ADDON_TABLE,
    ...TUI_DANISH_LANGUAGE_ADDON_COMMERCE,
    ...TUI_DANISH_LANGUAGE_ADDON_EDITOR,
    ...TUI_DANISH_LANGUAGE_LAYOUT,
};